import type { ReactNode } from 'react';
import styles from './KitQuestionList.module.css';

/**
 * An interview kit's questions as an interviewer reads them (INT-02): in the kit's order, under the rubric line each was
 * written for, numbered straight through. One component for the cockpit's kit sheet and for the kit editor's preview
 * (ADMIN-04), so the preview can never drift from what the panel really sees (admin redesign batch 2).
 */

export interface KitQuestionItem {
  readonly id: string;
  readonly text: string;
  readonly guidance: string | null;
  /** The line it was written under; null when that line is gone. */
  readonly topic: string | null;
}

export interface KitQuestionGroup {
  readonly topic: string;
  /** The number of its first question. */
  readonly start: number;
  readonly questions: readonly KitQuestionItem[];
}

export const OTHER_QUESTIONS = 'Other questions';

/** Runs of questions under the same line, in order. A kit saved by its editor already keeps each line's questions together. */
export function kitGroups(questions: readonly KitQuestionItem[]): readonly KitQuestionGroup[] {
  const groups: { topic: string; start: number; questions: KitQuestionItem[] }[] = [];
  questions.forEach((question, index) => {
    const topic = question.topic ?? OTHER_QUESTIONS;
    const last = groups.at(-1);
    if (last && last.topic === topic) last.questions.push(question);
    else groups.push({ topic, start: index + 1, questions: [question] });
  });
  return groups;
}

export function KitQuestionList({ questions, highlightId = null }: { readonly questions: readonly KitQuestionItem[]; readonly highlightId?: string | null }): ReactNode {
  return (
    <div className={styles.kit}>
      {kitGroups(questions).map((group) => (
        <section key={`${group.start}:${group.topic}`} className={styles.group} aria-label={group.topic}>
          <h3 className={styles.topic}>{group.topic}</h3>
          <ol className={styles.questions} start={group.start}>
            {group.questions.map((question) => (
              <li key={question.id} className={question.id === highlightId ? styles.highlight : undefined} aria-current={question.id === highlightId ? 'true' : undefined}>
                <p className={styles.question}>{question.text}</p>
                {question.guidance && <p className={styles.guidance}>Good answer: {question.guidance}</p>}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
